import React, { useState, useRef } from "react";
import isEmpty from "lodash/isEmpty";
import validateForm from "./utils/validateForm";
import Checkbox from "@micado-digital/react-form/Checkbox";
import Input from "@micado-digital/react-form/Input";
import Select from "@micado-digital/react-form/Select";
import Button from "@micado-digital/react-form/Button";
import Message from "@micado-digital/react-form/Message";
import useLocals from "@micado-digital/react-ui/hooks/useLocals";
import localDE from "../locals/de.json";
import localEN from "../locals/en.json";

import ReCAPTCHA from "react-google-recaptcha";
import styles from "./Form.styles";

const FormContent = ({ errorFields, formMessageObj, formDisabled, lang, title }) => {
	const css = styles();
	const redirectURL =
		lang === "de"
			? "/de/ihre-bestellung-ist-bei-uns-eingegangen.html"
			: "/en/we-have-received-your-order.html";

	const l = useLocals({
		lang,
		res: { de: localDE, en: localEN }
	});

	return (
		<>
			<input type="hidden" name="lang" value={lang} />
			<input type="hidden" name="layout" value="mail-shop" />
			<input type="hidden" name="subject" value="Shop - Bestellung" />
			<input type="hidden" name="productTitle" value={title || ""} />
			<input type="hidden" name="receiver" value="office@kohl-partner.at" />
			<input
				type="hidden"
				name="fields"
				value="productTitle=Produkt Titel;salutation=Anrede;firstName=Vorname;lastName=Nachname;email=E-Mail;company=Firma;street=Straße;postalCode=PLZ;town=Ort;country=Land;uid=UID-Nummer;privacy=on"
			/>
			{redirectURL && <input type="hidden" name="redir" value={redirectURL} />}

			<Select
				error={errorFields.includes("salutation")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.salutation")}
				name="salutation"
				required
				subItems={[
					{ "attr-value": "Frau", text: "Frau" },
					{ "attr-value": "Herr", text: "Herr" },
					{ "attr-value": "Familie", text: "Familie" }
				]}
				SelectProps={{
					fullWidth: true,
					variant: "outlined"
				}}
			/>

			<Input
				error={errorFields.includes("firstName")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.firstName")}
				name="firstName"
				required
				TextfieldProps={{ variant: "outlined" }}
			/>

			<Input
				error={errorFields.includes("lastName")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.lastName")}
				name="lastName"
				required
				TextfieldProps={{ variant: "outlined" }}
			/>

			<Input
				error={errorFields.includes("email")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.email")}
				name="email"
				required
				TextfieldProps={{ variant: "outlined" }}
			/>

			<Input
				error={errorFields.includes("company")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.company")}
				name="company"
				required
				TextfieldProps={{ variant: "outlined" }}
			/>

			<Input
				error={errorFields.includes("street")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.street")}
				name="street"
				required
				TextfieldProps={{ variant: "outlined" }}
			/>

			<Input
				error={errorFields.includes("postalCode")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.postalCode")}
				name="postalCode"
				required
				TextfieldProps={{ variant: "outlined" }}
			/>

			<Input
				error={errorFields.includes("town")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.town")}
				name="town"
				required
				TextfieldProps={{ variant: "outlined" }}
			/>

			<Select
				countryList
				error={errorFields.includes("country")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.country")}
				name="country"
				required
				SelectProps={{ variant: "outlined" }}
			/>

			<Input
				error={errorFields.includes("uid")}
				label={l("shopCustom.detail.form.uid")}
				name="uid"
				TextfieldProps={{ variant: "outlined" }}
			/>

			<Checkbox
				error={errorFields.includes("privacy")}
				errorMessage={l("shopCustom.detail.form.errorMessage")}
				label={l("shopCustom.detail.form.privacy")}
				name="privacy"
				required
			/>

			{!isEmpty(formMessageObj) && formMessageObj.message && (
				<Message text={formMessageObj.message} type={formMessageObj.type} />
			)}

			<div className={css.button}>
				<Button
					disabled={formDisabled}
					label={l("shopCustom.detail.form.button")}
					ButtonProps={{ color: "primary", variant: "contained" }}
					type="submit"
				/>
			</div>
		</>
	);
};

const Form = ({ lang, title }) => {
	const { REACT_APP_PATH } = process.env;

	const recaptchaRef = useRef(null);
	const formRef = useRef(null);

	const [errorFields, setErrorFields] = useState([]);
	const [formDisabled, setFormDisabled] = useState(false);
	const [formMessageObj, setFormMessageObj] = useState({});

	const l = useLocals({
		lang,
		res: { de: localDE, en: localEN }
	});

	const css = styles();

	// Wird ausgelöst wenn Invisible reCAPTCHA completed ist
	const onRecaptchaChange = token => {
		if (token) {
			formRef.current.submit();
		} else {
			setFormDisabled(false);
		}
	};

	const handleSubmitForm = e => {
		e.preventDefault();

		const { error, errorFields } = validateForm(e.target);

		if (error) {
			setErrorFields(errorFields);
			setFormMessageObj({
				state: "error",
				msg: l("shopCustom.detail.form.validationErrorMessage")
			});
			return;
		}

		setFormDisabled(true);

		// Invisible reCAPTCHA ausführen
		if (recaptchaRef.current) {
			recaptchaRef.current.execute();
		}
	};

	return (
		<div className={css.form}>
			<form
				ref={formRef}
				className={css.form}
				action={`${REACT_APP_PATH}/sendmail.api`}
				method="POST"
				encType="multipart/form-data"
				onSubmit={handleSubmitForm}
			>
				<FormContent
					errorFields={errorFields}
					formDisabled={formDisabled}
					formMessageObj={formMessageObj}
					lang={lang}
					title={title}
				/>
				<ReCAPTCHA
					ref={recaptchaRef}
					size="invisible"
					sitekey="6LeKXFYeAAAAAG904C4aDK3thgznQZrPha08cNoh"
					onChange={onRecaptchaChange}
				/>
			</form>
		</div>
	);
};

export default Form;
